import React from 'react';
import './AllProjectsPopup.css';

const AllProjectsPopup = ({ isOpen, onClose, userProjects, onEditProject, onDeleteProject }) => {
  if (!isOpen) return null;

  const formatDateRange = (startDate, endDate, isOngoing) => {
    const formatDate = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short' 
      });
    };

    const start = formatDate(startDate);
    
    if (isOngoing) {
      return (
        <>
          {start} - <span className="present-badge">Present</span>
        </>
      );
    } else {
      const end = formatDate(endDate);
      return `${start} - ${end}`;
    }
  };

  const getProjectTypeColor = (type) => {
    const colors = {
      personal: '#3b82f6',
      academic: '#10b981',
      final_year: '#f59e0b',
      hackathon: '#ef4444',
      open_source: '#8b5cf6',
    };
    return colors[type] || '#6b7280';
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: '#10b981',
      ongoing: '#3b82f6',
      abandoned: '#ef4444',
    };
    return colors[status] || '#6b7280';
  };

  return (
    <div className="all-projects-popup-overlay">
      <div className="all-projects-popup">
        <div className="all-projects-popup-header">
          <h3>All Projects ({userProjects.length})</h3>
          <button className="all-projects-popup-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="all-projects-popup-content">
          {userProjects.length === 0 ? (
            <div className="all-projects-empty">
              <div className="all-projects-empty-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"></path>
                  <polyline points="14,2 14,8 20,8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10,9 9,9 8,9"></polyline>
                </svg>
              </div>
              <h4>No Projects Yet</h4>
              <p>Start building your portfolio by adding your first project!</p>
            </div>
          ) : (
            <div className="all-projects-list">
              {userProjects.map((project, index) => (
                <div key={project.id} className="all-project-card">
                  <div className="all-project-header">
                    <div className="all-project-title-section">
                      <h4 className="all-project-title">{project.title}</h4>
                      <div className="all-project-meta">
                        <span 
                          className="all-project-type-badge"
                          style={{ backgroundColor: getProjectTypeColor(project.project_type) }}
                        >
                          {project.formatted_project_type}
                        </span>
                        <span 
                          className="all-project-status-badge"
                          style={{ backgroundColor: getStatusColor(project.status) }}
                        >
                          {project.formatted_status}
                        </span>
                        <span className="all-project-date">
                          {formatDateRange(project.start_date, project.end_date, project.is_ongoing)}
                        </span>
                      </div>
                    </div>
                    <div className="all-project-actions">
                      <button 
                        className="all-edit-project-btn"
                        onClick={() => {
                          onEditProject(project);
                          onClose();
                        }}
                        title="Edit project"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                        </svg>
                      </button>
                      <button 
                        className="all-remove-project-btn"
                        onClick={() => onDeleteProject(project.id)}
                        title="Remove project"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3,6 5,6 21,6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      </button>
                    </div>
                  </div>

                  {project.description && (
                    <div className="all-project-description">
                      <p>{project.description}</p>
                    </div>
                  )}

                  {project.technologies_used && project.technologies_used.length > 0 && (
                    <div className="all-project-technologies">
                      <div className="all-project-tech-list">
                        {project.technologies_used.map((tech, techIndex) => (
                          <span key={techIndex} className="all-project-tech-tag">
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="all-project-links">
                    {project.github_url && (
                      <a 
                        href={project.github_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="all-project-link github-link"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                        GitHub
                      </a>
                    )}
                    {project.live_url && (
                      <a 
                        href={project.live_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="all-project-link live-link"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                          <polyline points="15,3 21,3 21,9"></polyline>
                          <line x1="10" y1="14" x2="21" y2="3"></line>
                        </svg>
                        Live Demo
                      </a>
                    )}
                    {project.demo_url && (
                      <a 
                        href={project.demo_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="all-project-link demo-link"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5,3 19,12 5,21 5,3"></polygon>
                        </svg>
                        Demo
                      </a>
                    )}
                  </div>

                  {/* Project Screenshots */}
                  {project.screenshots && project.screenshots.length > 0 && (
                    <div className="all-project-screenshots">
                      <h5>Screenshots:</h5>
                      <div className="all-screenshots-grid">
                        {project.screenshots.slice(0, 4).map((screenshot, screenshotIndex) => (
                          <div key={screenshotIndex} className="all-screenshot-item">
                            <img 
                              src={screenshot} 
                              alt={`${project.title} screenshot ${screenshotIndex + 1}`}
                              className="all-screenshot-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        ))}
                        {project.screenshots.length > 4 && (
                          <div className="all-screenshot-more">
                            <span>+{project.screenshots.length - 4} more</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllProjectsPopup;
